import CheckIcon from '@mui/icons-material/Check'
import Chip, { ChipProps } from '@mui/material/Chip'
import { blue, common, cyan, deepOrange, orange, red } from '@mui/material/colors'
import { getContrastRatio } from '@mui/material/styles'
import React from 'react'

import { COMPLETE, ERROR, GREY, INFO, TEXT, UNASSIGNED, WARNING } from 'theme/colors'

// `auto-confirmed` / `modified` sit next to confirmed/pending but stay visually distinct.
export type StatusTone = 'pending' | 'confirmed' | 'auto-confirmed' | 'rejected' | 'info' | 'modified' | 'neutral'

export interface StatusChipProps {
  /** Visual tone — drives bg + text colors via theme tokens. */
  tone: StatusTone
  label: string
  /** Show a check icon to the left (typically used with `confirmed`). */
  showCheck?: boolean
  /**
   * Black/white text by WCAG contrast against the chip bg. Opt-in so existing
   * soft same-hue consumers stay unchanged; Vision review badges set it.
   */
  highContrast?: boolean
  /** Forwarded to MUI `Chip`. */
  sx?: ChipProps['sx']
}

// Black or white, whichever contrasts better with `bg`.
const bestContrastText = (bg: string, fallback: string): string => {
  try {
    return getContrastRatio(bg, common.white) >= getContrastRatio(bg, common.black) ? common.white : common.black
  } catch {
    return fallback
  }
}

const TONE_COLORS: Record<StatusTone, { bg: string; color: string }> = {
  pending: { bg: WARNING.light, color: UNASSIGNED.main },
  confirmed: { bg: COMPLETE.dark, color: COMPLETE.main },
  'auto-confirmed': { bg: cyan[50], color: cyan[800] },
  rejected: { bg: red[50], color: ERROR.main },
  info: { bg: blue[50], color: INFO.dark },
  modified: { bg: orange[50], color: deepOrange[900] },
  neutral: { bg: GREY[200], color: TEXT.lightBrown },
}

/**
 * Pill-shaped status indicator built on MUI `Chip` (matches the pattern used
 * by InvoiceStatusChip / PaymentStatusChip / SuspensionChip in
 * `src/components/common/chip/`). Tones resolve to theme tokens (`theme/colors`)
 * and MUI stock palettes (`@mui/material/colors`) — no hex literals.
 */
const StatusChip: React.FC<StatusChipProps> = ({ tone, label, showCheck = false, highContrast = false, sx }) => {
  const palette = TONE_COLORS[tone]
  const textColor = highContrast ? bestContrastText(palette.bg, palette.color) : palette.color
  return (
    <Chip
      icon={showCheck ? <CheckIcon sx={{ fontSize: 12 }} /> : undefined}
      label={label}
      size="small"
      sx={{
        bgcolor: palette.bg,
        color: textColor,
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        height: 22,
        // The default MUI chip icon margin is 5px on the left; tighten it so
        // the check sits next to the label without extra slack.
        '& .MuiChip-icon': {
          color: textColor,
          marginLeft: '6px',
          marginRight: '-4px',
        },
        '& .MuiChip-label': {
          paddingLeft: '8px',
          paddingRight: '10px',
        },
        ...sx,
      }}
    />
  )
}

export { StatusChip }
